import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { EventInboxApiFactory } from '../api/eventInboxApi';
import type { EventInboxDetail } from '../eventInboxTypes';

interface UseGetEventInboxByIdResult {
  data: EventInboxDetail | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Fetches a single Event Inbox entry. A 404 (event absent in the active tenant)
 * surfaces as an `ApiError` with `isNotFound` — the detail view renders a clean
 * empty/error state for it.
 */
export function useGetEventInboxById(id: string | undefined): UseGetEventInboxByIdResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => EventInboxApiFactory(client), [client]);

  const [data, setData] = useState<EventInboxDetail | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    if (!id) return;

    const ctrl = new AbortController();
    setData(null);
    setError(null);

    api
      .getEventById(id, { signal: ctrl.signal })
      .then(setData)
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setData(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api, id]);

  return { data, loading, error };
}
